import React, { useState } from 'react'

import {
    Autocomplete,
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    IconButton
} from '@mui/material'

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const EMPTY_VALUE_OPERATORS = ['IS_EMPTY', 'IS_NOT_EMPTY']

const TEXT_OPERATOR_OPTIONS = [
    { value: 'EQUALS', label: 'Igual' },
    { value: 'NOT_EQUALS', label: 'Diferente' },
    { value: 'CONTAINS', label: 'Contém' },
    { value: 'NOT_CONTAINS', label: 'Não contém' },
]

const ORDER_OPERATOR_OPTIONS = [
    { value: 'EQUALS', label: 'Igual' },
    { value: 'NOT_EQUALS', label: 'Diferente' },
    { value: 'GREATER_THAN', label: 'Maior' },
    { value: 'LESS_THAN', label: 'Menor' },
]

const DATE_OPERATOR_OPTIONS = [
    { value: 'GREATER_THAN', label: 'Maior' },
    { value: 'LESS_THAN', label: 'Menor' },
]

const PHONE_OPERATOR_OPTIONS = [
    { value: 'EQUALS', label: 'Igual' },
    { value: 'NOT_EQUALS', label: 'Diferente' },
]

const RELATION_OPERATOR_OPTIONS = [
    { value: 'EQUALS', label: 'Igual' },
    { value: 'NOT_EQUALS', label: 'Diferente' },
]

const OPERATOR_OPTIONS = {
    text: TEXT_OPERATOR_OPTIONS,
    phone: PHONE_OPERATOR_OPTIONS,
    number: ORDER_OPERATOR_OPTIONS,
    date: ORDER_OPERATOR_OPTIONS,
    birthday: RELATION_OPERATOR_OPTIONS,
    relation: RELATION_OPERATOR_OPTIONS,
    boolean: RELATION_OPERATOR_OPTIONS,
    phone_presence: RELATION_OPERATOR_OPTIONS,
    select: RELATION_OPERATOR_OPTIONS,
}

const NATIVE_FIELDS = [
    { value: 'contact_group', label: 'Segmento(s)', type: 'relation', valueType: 'groups', valueLabel: 'Segmentos' },
    { value: 'contact_enterprise', label: 'Empresa(s)', type: 'relation', valueType: 'enterprises', valueLabel: 'Empresas' },
    { value: 'email', label: 'Que possuam e-mail com final', type: 'text', valueLabel: 'Final do e-mail' },
    { value: 'telephone', label: 'Que possuam telefone com DDD', type: 'phone', valueLabel: 'DDD', operatorOptions: PHONE_OPERATOR_OPTIONS },
    { value: 'created_at', label: 'Data de criação', type: 'date', valueLabel: 'Data', operatorOptions: DATE_OPERATOR_OPTIONS },
    { value: 'contact_owner', label: 'Dono do contato', type: 'relation', valueType: 'users', valueLabel: 'Donos do contato' },
    { value: 'birthday', label: 'Aniversariantes do mês', type: 'birthday', valueLabel: 'Mês', hideOperator: true, defaultOperator: 'EQUALS' },
    { value: 'phone_presence', label: 'Contato com/sem telefone', type: 'phone_presence', valueLabel: 'Telefone', hideOperator: true, defaultOperator: 'EQUALS' },
]

const DEFAULT_OPERATOR_BY_FIELD = {
    birthday: 'EQUALS',
    phone_presence: 'EQUALS',
}

const ALLOWED_OPERATOR_BY_FIELD = {
    telephone: PHONE_OPERATOR_OPTIONS.map(option => option.value),
    created_at: DATE_OPERATOR_OPTIONS.map(option => option.value),
    birthday: ['EQUALS'],
    phone_presence: ['EQUALS'],
}

const MONTHS = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
]

const PHONE_PRESENCE_OPTIONS = [
    { value: 'has_phone', label: 'Com telefone cadastrado' },
    { value: 'no_phone', label: 'Sem telefone cadastrado' },
]

const getCustomFieldName = (field) => field.field_name || field.name || field.label || `Campo ${field.id}`

const getCustomFieldType = (field) => {
    if (field.field_type === 'integer')
        return 'number'
    if (field.field_type === 'textarea' || field.field_type === 'string')
        return 'text'
    if (field.field_type === 'select')
        return 'select'
    if (field.field_type === 'boolean' || field.field_type === 'checkbox')
        return 'boolean'
    if (field.field_type === 'date')
        return 'date'
    return 'text'
}

const getFieldOptions = (customFields) => ([
    ...NATIVE_FIELDS,
    ...(customFields || []).map(field => ({
        value: `custom_field_${field.id}`,
        label: `Campo personalizado: ${getCustomFieldName(field)}`,
        type: getCustomFieldType(field),
        options: field.options || [],
        customFieldId: field.id,
        valueLabel: 'Valor',
    }))
])

const getConditionFieldKey = (condition) => {
    if (condition?.field === 'custom_field')
        return condition.custom_field_id ? `custom_field_${condition.custom_field_id}` : ''
    return condition?.field || ''
}

const normalizeValueList = (value) => {
    if (Array.isArray(value))
        return value.map(each => String(each))
    if (value === undefined || value === null || value === '')
        return []
    return String(value).split(',').map(each => each.trim()).filter(Boolean)
}

const normalizeOptionList = (value) => {
    if (!Array.isArray(value))
        return []
    return value
        .filter(option => option?.id !== undefined && option?.id !== null)
        .map(option => ({
            ...option,
            id: String(option.id),
            label: option.label || option.name || option.contact_enterprise_name || String(option.id),
        }))
}

const getOptionLabel = (options, value) => {
    const option = options.find(each => String(each.id) === String(value))
    return option?.name || option?.contact_enterprise_name || option?.label || value
}

const getAutocompleteOptionLabel = (option) => option?.name || option?.contact_enterprise_name || option?.label || ''

const getUserLabel = (user) => {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
    return name || user.username
}

const getEmptyCondition = () => ({ field: '', custom_field_id: '', operator: '', value: '' })

const normalizeOperator = (operator) => {
    if (operator === 'IN')
        return 'EQUALS'
    if (operator === 'NOT_IN')
        return 'NOT_EQUALS'
    return operator || ''
}

const getConditionOperator = (condition) => {
    const operator = normalizeOperator(condition?.operator) || DEFAULT_OPERATOR_BY_FIELD[condition?.field] || ''
    const allowedOperators = ALLOWED_OPERATOR_BY_FIELD[condition?.field]
    if (allowedOperators && !allowedOperators.includes(operator))
        return DEFAULT_OPERATOR_BY_FIELD[condition?.field] || ''
    return operator
}

export const normalizeContactCondition = (condition) => {
    if (!condition || Object.keys(condition).length === 0)
        return getEmptyCondition()
    return {
        field: condition.field || '',
        custom_field_id: condition.custom_field_id || '',
        operator: normalizeOperator(condition.operator),
        value: condition.value ?? '',
        value_options: normalizeOptionList(condition.value_options),
    }
}

export const normalizeContactConditions = (condition, keepEmpty = false) => {
    if (!condition)
        return []
    if (Array.isArray(condition))
        return condition.map(normalizeContactCondition).filter(item => keepEmpty || isContactConditionStarted(item))
    if (Array.isArray(condition.conditions))
        return normalizeContactConditions(condition.conditions, keepEmpty)

    const normalized = normalizeContactCondition(condition)
    return keepEmpty || isContactConditionStarted(normalized) ? [normalized] : []
}

export const isContactConditionStarted = (condition) => Boolean(
    condition?.field ||
    condition?.custom_field_id ||
    condition?.operator ||
    (condition?.value !== undefined && condition?.value !== null && condition?.value !== '')
)

export const getContactConditionErrors = (conditions) => {
    const errors = []

    normalizeContactConditions(conditions, true).forEach((condition, index) => {
        const number = index + 1
        if (!condition.field)
            errors.push(`Selecione o campo da condição do contato ${number}.`)
        if (!getConditionOperator(condition))
            errors.push(`Selecione o operador da condição do contato ${number}.`)
        if (condition.field === 'custom_field' && !condition.custom_field_id)
            errors.push(`Selecione o campo personalizado da condição do contato ${number}.`)

        const needsValue = !EMPTY_VALUE_OPERATORS.includes(getConditionOperator(condition))
        const value = condition.value
        const isEmptyValue = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
        if (needsValue && isEmptyValue)
            errors.push(`Informe o valor da condição do contato ${number}.`)
    })

    return errors
}

export const buildContactConditionPayload = (conditions) => normalizeContactConditions(conditions).map(condition => ({
    field: condition.field,
    custom_field_id: condition.custom_field_id || undefined,
    operator: getConditionOperator(condition),
    value: EMPTY_VALUE_OPERATORS.includes(getConditionOperator(condition)) ? '' : condition.value,
    value_options: condition.value_options || [],
}))

const ContactConditionRow = ({
    condition,
    index,
    onChange,
    onRemove,
    fieldOptions,
    groups,
    enterprises,
    users,
    onGroupInputChange,
    onEnterpriseInputChange,
    onUserInputChange,
}) => {
    const conditionFieldKey = getConditionFieldKey(condition)
    const selectedField = fieldOptions.find(field => field.value === conditionFieldKey)
    const operators = selectedField ? selectedField.operatorOptions || OPERATOR_OPTIONS[selectedField.type] || OPERATOR_OPTIONS.text : []
    const operator = operators.some(option => option.value === condition.operator) ? condition.operator : ''
    const effectiveOperator = operator || selectedField?.defaultOperator || DEFAULT_OPERATOR_BY_FIELD[condition.field] || ''
    const showOperator = selectedField && !selectedField.hideOperator
    const needsValue = effectiveOperator && !EMPTY_VALUE_OPERATORS.includes(effectiveOperator)
    const [listInputValue, setListInputValue] = useState('')
    const [selectInputValue, setSelectInputValue] = useState(undefined)

    const changeField = (value) => {
        setListInputValue('')
        setSelectInputValue(undefined)

        if (!value) {
            onRemove()
            return
        }

        const nextField = fieldOptions.find(field => field.value === value)
        const operator = nextField?.hideOperator ? nextField.defaultOperator : ''

        if (String(value).startsWith('custom_field_')) {
            onChange({
                field: 'custom_field',
                custom_field_id: Number(String(value).replace('custom_field_', '')),
                operator,
                value: '',
                value_options: [],
            })
            return
        }

        onChange({
            field: value,
            custom_field_id: '',
            operator,
            value: '',
            value_options: [],
        })
    }

    const changeOperator = (value) => {
        onChange({
            ...condition,
            operator: value,
            value: EMPTY_VALUE_OPERATORS.includes(value) ? '' : condition.value,
            value_options: EMPTY_VALUE_OPERATORS.includes(value) ? [] : condition.value_options,
        })
    }

    const renderMultipleValue = (options, extraOptions = []) => {
        const normalizedValue = normalizeValueList(condition.value)
        const savedOptions = normalizeOptionList(condition.value_options)
        const allOptions = [...extraOptions, ...savedOptions, ...(options || [])]
        const selectedOptions = normalizedValue.map(value => (
            allOptions.find(option => String(option.id) === String(value)) || { id: value, label: getOptionLabel(allOptions, value) }
        ))
        const autocompleteOptions = [
            ...selectedOptions.filter(selected => !allOptions.some(option => String(option.id) === String(selected.id))),
            ...allOptions,
        ]

        return (
            <Autocomplete
                size="small"
                fullWidth
                multiple
                disabled={!needsValue}
                options={autocompleteOptions}
                filterSelectedOptions
                filterOptions={(options) => options}
                value={selectedOptions}
                inputValue={listInputValue}
                getOptionLabel={getAutocompleteOptionLabel}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="Nenhuma opção disponível"
                onChange={(event, newValue) => {
                    setListInputValue('')
                    onChange({
                        ...condition,
                        value: newValue.map(option => String(option.id)),
                        value_options: newValue.map(option => ({
                            id: String(option.id),
                            label: getAutocompleteOptionLabel(option) || String(option.id),
                        })),
                    })
                }}
                onInputChange={(event, value, reason) => {
                    if (reason === 'reset')
                        return
                    setListInputValue(value)
                    if (selectedField.valueType === 'groups')
                        onGroupInputChange(value)
                    if (selectedField.valueType === 'enterprises')
                        onEnterpriseInputChange(value)
                    if (selectedField.valueType === 'users')
                        onUserInputChange(value)
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={selectedField.valueLabel}
                        InputProps={{
                            ...params.InputProps,
                            type: 'search',
                        }}
                    />
                )}
            />
        )
    }

    const renderValueInput = () => {
        if (!selectedField || !needsValue)
            return (
                <TextField
                    disabled
                    label="Valor"
                    size="small"
                    value=""
                    fullWidth
                />
            )

        if (selectedField.valueType === 'groups')
            return renderMultipleValue(groups)

        if (selectedField.valueType === 'enterprises')
            return renderMultipleValue(enterprises)

        if (selectedField.valueType === 'users')
            return renderMultipleValue(
                (users || []).map(user => ({ ...user, name: getUserLabel(user) })),
                [{ id: 'public', name: 'Público' }]
            )

        if (selectedField.type === 'boolean')
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel id={`contact-condition-boolean-value-${index}`}>Valor</InputLabel>
                    <Select
                        labelId={`contact-condition-boolean-value-${index}`}
                        label="Valor"
                        value={condition.value}
                        onChange={({ target }) => onChange({ ...condition, value: target.value })}
                    >
                        <MenuItem value="1">Sim</MenuItem>
                        <MenuItem value="0">Não</MenuItem>
                    </Select>
                </FormControl>
            )

        if (selectedField.type === 'birthday')
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel id={`contact-condition-birthday-value-${index}`}>Mês</InputLabel>
                    <Select
                        labelId={`contact-condition-birthday-value-${index}`}
                        label="Mês"
                        value={condition.value}
                        onChange={({ target }) => onChange({ ...condition, value: target.value })}
                    >
                        {MONTHS.map(month => (
                            <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )

        if (selectedField.type === 'phone_presence')
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel id={`contact-condition-phone-presence-value-${index}`}>Telefone</InputLabel>
                    <Select
                        labelId={`contact-condition-phone-presence-value-${index}`}
                        label="Telefone"
                        value={condition.value}
                        onChange={({ target }) => onChange({ ...condition, value: target.value })}
                    >
                        {PHONE_PRESENCE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )

        if (selectedField.type === 'select')
        {
            const selectOptions = selectedField.options || []
            const selectedOption = selectOptions.find(option => String(option.value) === String(condition.value)) || null
            const selectedOptionLabel = selectedOption?.label || selectedOption?.value || ''
            return (
                <Autocomplete
                    size="small"
                    fullWidth
                    options={selectOptions}
                    value={selectedOption}
                    inputValue={selectInputValue === undefined ? selectedOptionLabel : selectInputValue}
                    getOptionLabel={(option) => option?.label || option?.value || ''}
                    isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                    noOptionsText="Nenhuma opção disponível"
                    onChange={(event, newValue) => {
                        setSelectInputValue(newValue?.label || newValue?.value || '')
                        onChange({ ...condition, value: newValue?.value || '' })
                    }}
                    onInputChange={(event, value, reason) => {
                        if (reason !== 'reset')
                            setSelectInputValue(value)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Valor"
                            InputProps={{
                                ...params.InputProps,
                                type: 'search',
                            }}
                        />
                    )}
                />
            )
        }

        return (
            <TextField
                label={selectedField.valueLabel || 'Valor'}
                size="small"
                type={selectedField.type === 'number' ? 'number' : selectedField.type === 'date' ? 'date' : 'text'}
                value={condition.value}
                onChange={({ target }) => {
                    if (selectedField.type === 'phone' && !/^\d{0,2}$/.test(target.value))
                        return
                    onChange({ ...condition, value: target.value })
                }}
                InputLabelProps={selectedField.type === 'date' ? { shrink: true } : undefined}
                inputProps={selectedField.type === 'phone' ? { maxLength: 2, inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
                fullWidth
            />
        )
    }

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" gap={1} alignItems="center">
                <FormControl size="small" sx={{ flex: 2 }}>
                    <InputLabel id={`contact-condition-field-${index}`}>Campo</InputLabel>
                    <Select
                        labelId={`contact-condition-field-${index}`}
                        label="Campo"
                        value={conditionFieldKey}
                        onChange={({ target }) => changeField(target.value)}
                    >
                        <MenuItem value="">Sem condição</MenuItem>
                        {fieldOptions.map(field => (
                            <MenuItem key={field.value} value={field.value}>{field.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {showOperator &&
                    <FormControl size="small" sx={{ flex: 1 }} disabled={!selectedField}>
                        <InputLabel id={`contact-condition-operator-${index}`}>Operador</InputLabel>
                        <Select
                            labelId={`contact-condition-operator-${index}`}
                            label="Operador"
                            value={operator}
                            onChange={({ target }) => changeOperator(target.value)}
                        >
                            {operators.map(operator => (
                                <MenuItem key={operator.value} value={operator.value}>{operator.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                }
                <IconButton onClick={onRemove}>
                    <RemoveIcon />
                </IconButton>
            </Box>
            {renderValueInput()}
        </Box>
    )
}

const ContactCondition = ({
    conditions,
    condition,
    onChange,
    customFields,
    groups,
    enterprises,
    users,
    onGroupInputChange = () => {},
    onEnterpriseInputChange = () => {},
    onUserInputChange = () => {},
}) => {
    const value = normalizeContactConditions(conditions ?? condition, true)
    const fieldOptions = getFieldOptions(customFields)

    const updateCondition = (index, nextCondition) => {
        onChange(value.map((item, itemIndex) => itemIndex === index ? nextCondition : item))
    }

    const removeCondition = (index) => {
        onChange(value.filter((item, itemIndex) => itemIndex !== index))
    }

    const addCondition = () => {
        onChange([...value, getEmptyCondition()])
    }

    return (
        <Box display="flex" flexDirection="column" gap={1} p={2} border="1px solid #c4c4c4" borderRadius="4px">
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box className="category-title">
                    Condições do contato (opcional)
                </Box>
                <Button
                    size="small"
                    variant="contained"
                    onClick={addCondition}
                    startIcon={<AddIcon />}
                >
                    Condição
                </Button>
            </Box>
            {value.length === 0 && (
                <TextField
                    disabled
                    label="Condição"
                    size="small"
                    value="Sem condição"
                    fullWidth
                />
            )}
            {value.map((item, index) => (
                <ContactConditionRow
                    key={index}
                    condition={item}
                    index={index}
                    onChange={(nextCondition) => updateCondition(index, nextCondition)}
                    onRemove={() => removeCondition(index)}
                    fieldOptions={fieldOptions}
                    groups={groups}
                    enterprises={enterprises}
                    users={users}
                    onGroupInputChange={onGroupInputChange}
                    onEnterpriseInputChange={onEnterpriseInputChange}
                    onUserInputChange={onUserInputChange}
                />
            ))}
        </Box>
    )
}

export default ContactCondition
